import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createInterviewCandidateAPI,
  getInterviewCandidateListAPI,
  addOrUpdateCompanyQuotaAPI,
  cancelInterviewCandidateAPI,
  getCandidateAssessmentLinkAPI,
  getCandidateResultAPI,
} from "./interViewCandidateApi";

// Create Interview Candidate
export const createInterviewCandidate = createAsyncThunk(
  "interviewCandidate/createInterviewCandidate",
  async (candidateData, { rejectWithValue }) => {
    try {
      const response = await createInterviewCandidateAPI(candidateData);
      return response;
    } catch (error) {
      console.error("❌ Thunk Error:", error);
      console.error("❌ Error Response:", error.response);
      
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.msg ||
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        (typeof error.response?.data === 'string' ? error.response?.data : null) ||
        error.message ||
        "Failed to create interview candidate";
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Get Interview Candidate List
export const getInterviewCandidateList = createAsyncThunk(
  "interviewCandidate/getInterviewCandidateList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getInterviewCandidateListAPI(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch interview candidate list"
      );
    }
  }
);

// Add or Update Company Quota
export const addOrUpdateCompanyQuota = createAsyncThunk(
  "interviewCandidate/addOrUpdateCompanyQuota",
  async (quotaData, { rejectWithValue }) => {
    try {
      console.log("🚀 Thunk: addOrUpdateCompanyQuota called with:", quotaData);
      const response = await addOrUpdateCompanyQuotaAPI(quotaData);
      console.log("✅ Thunk: API Response:", response);
      return response;
    } catch (error) {
      console.error("❌ Thunk: API Error:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to add/update company quota"
      );
    }
  }
);

// Cancel Interview Candidate
export const cancelInterviewCandidate = createAsyncThunk(
  "interviewCandidate/cancelInterviewCandidate",
  async (cancelData, { rejectWithValue }) => {
    try {
      const response = await cancelInterviewCandidateAPI(cancelData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel interview candidate"
      );
    }
  }
);

// Get Candidate Assessment Link
export const getCandidateAssessmentLink = createAsyncThunk(
  "interviewCandidate/getCandidateAssessmentLink",
  async (assessmentData, { rejectWithValue }) => {
    try {
      console.log("🚀 Thunk: getCandidateAssessmentLink called with:", assessmentData);
      const response = await getCandidateAssessmentLinkAPI(assessmentData);
      console.log("✅ Thunk: API Response:", response);
      return response;
    } catch (error) {
      console.error("❌ Thunk: API Error:", error);
      
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.msg ||
        error.message ||
        "Failed to get candidate assessment link";
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Get Candidate Result
export const getCandidateResult = createAsyncThunk(
  "interviewCandidate/getCandidateResult",
  async (resultData, { rejectWithValue }) => {
    try {
      console.log("🚀 Thunk: getCandidateResult called with:", resultData);
      const response = await getCandidateResultAPI(resultData);
      console.log("✅ Thunk: API Response:", response);
      return response;
    } catch (error) {
      console.error("❌ Thunk: API Error:", error);
      
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.msg ||
        error.message ||
        "Failed to get candidate result";
      
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  createCandidate: { 
    data: null, 
    loading: false, 
    error: null, 
    success: false 
  },
  candidateList: { 
    data: [], 
    loading: false, 
    error: null,
    pagination: null 
  },
  addUpdateQuota: { 
    data: null, 
    loading: false, 
    error: null, 
    success: false 
  },
  cancelCandidate: { 
    data: null, 
    loading: false, 
    error: null, 
    success: false 
  },
  assessmentLink: {
    data: null,
    loading: false,
    error: null,
    success: false
  },
  candidateResult: {
    data: null,
    loading: false,
    error: null,
    success: false
  },
};

const interviewCandidateSlice = createSlice({
  name: "interviewCandidate",
  initialState,
  reducers: {
    clearCreateCandidateState: (state) => {
      state.createCandidate = { 
        data: null, 
        loading: false, 
        error: null, 
        success: false 
      };
    },
    clearCandidateList: (state) => {
      state.candidateList = { 
        data: [], 
        loading: false, 
        error: null,
        pagination: null 
      };
    },
    clearAddUpdateQuotaState: (state) => {
      console.log("🧹 Clearing addUpdateQuota state");
      state.addUpdateQuota = { 
        data: null, 
        loading: false, 
        error: null, 
        success: false 
      };
    },
    clearCancelCandidateState: (state) => {
      state.cancelCandidate = { 
        data: null, 
        loading: false, 
        error: null, 
        success: false 
      };
    },
    clearAssessmentLinkState: (state) => {
      console.log("🧹 Clearing assessmentLink state");
      state.assessmentLink = {
        data: null,
        loading: false,
        error: null,
        success: false
      };
    },
    clearCandidateResultState: (state) => {
      console.log("🧹 Clearing candidateResult state");
      state.candidateResult = {
        data: null,
        loading: false,
        error: null,
        success: false
      };
    },
  },
  extraReducers: (builder) => {
    // Create Interview Candidate
    builder
      .addCase(createInterviewCandidate.pending, (state) => {
        state.createCandidate.loading = true;
        state.createCandidate.error = null;
        state.createCandidate.success = false;
      })
      .addCase(createInterviewCandidate.fulfilled, (state, action) => {
        state.createCandidate.loading = false;
        state.createCandidate.data = action.payload;
        state.createCandidate.success = true;
      })
      .addCase(createInterviewCandidate.rejected, (state, action) => {
        state.createCandidate.loading = false;
        state.createCandidate.error = action.payload;
        state.createCandidate.success = false;
      });

    // Get Interview Candidate List
    builder
      .addCase(getInterviewCandidateList.pending, (state) => {
        state.candidateList.loading = true;
        state.candidateList.error = null;
      })
      .addCase(getInterviewCandidateList.fulfilled, (state, action) => {
        state.candidateList.loading = false;
        state.candidateList.data = action.payload?.data?.data || action.payload?.data || [];
        state.candidateList.pagination = {
          page: action.payload?.data?.page || 1,
          limit: action.payload?.data?.limit || 10,
          total: action.payload?.data?.total || 0,
          totalPages: action.payload?.data?.totalPages || 1,
        };
        state.candidateList.quota = action.payload?.data?.quota || { total_quota: 0, used_quota: 0 };
        state.candidateList.total = action.payload?.data?.total || 0;
        state.candidateList.totalPages = action.payload?.data?.totalPages || 1;
      })
      .addCase(getInterviewCandidateList.rejected, (state, action) => {
        state.candidateList.loading = false;
        state.candidateList.error = action.payload;
      });

    // Add or Update Company Quota
    builder
      .addCase(addOrUpdateCompanyQuota.pending, (state) => {
        console.log("⏳ addOrUpdateCompanyQuota: PENDING");
        state.addUpdateQuota.loading = true;
        state.addUpdateQuota.error = null;
        state.addUpdateQuota.success = false;
        state.addUpdateQuota.data = null;
      })
      .addCase(addOrUpdateCompanyQuota.fulfilled, (state, action) => {
        console.log("✅ addOrUpdateCompanyQuota: FULFILLED", action.payload);
        state.addUpdateQuota.loading = false;
        state.addUpdateQuota.data = action.payload;
        state.addUpdateQuota.success = true;
        state.addUpdateQuota.error = null;
      })
      .addCase(addOrUpdateCompanyQuota.rejected, (state, action) => {
        console.log("❌ addOrUpdateCompanyQuota: REJECTED", action.payload);
        state.addUpdateQuota.loading = false;
        state.addUpdateQuota.error = action.payload;
        state.addUpdateQuota.success = false;
        state.addUpdateQuota.data = null;
      });

    // Cancel Interview Candidate
    builder
      .addCase(cancelInterviewCandidate.pending, (state) => {
        state.cancelCandidate.loading = true;
        state.cancelCandidate.error = null;
        state.cancelCandidate.success = false;
      })
      .addCase(cancelInterviewCandidate.fulfilled, (state, action) => {
        state.cancelCandidate.loading = false;
        state.cancelCandidate.data = action.payload;
        state.cancelCandidate.success = true;
      })
      .addCase(cancelInterviewCandidate.rejected, (state, action) => {
        state.cancelCandidate.loading = false;
        state.cancelCandidate.error = action.payload;
        state.cancelCandidate.success = false;
      });

    // Get Candidate Assessment Link
    builder
      .addCase(getCandidateAssessmentLink.pending, (state) => {
        console.log("⏳ getCandidateAssessmentLink: PENDING");
        state.assessmentLink.loading = true;
        state.assessmentLink.error = null;
        state.assessmentLink.success = false;
        state.assessmentLink.data = null;
      })
      .addCase(getCandidateAssessmentLink.fulfilled, (state, action) => {
        console.log("✅ getCandidateAssessmentLink: FULFILLED", action.payload);
        state.assessmentLink.loading = false;
        state.assessmentLink.data = action.payload;
        state.assessmentLink.success = true;
        state.assessmentLink.error = null;
      })
      .addCase(getCandidateAssessmentLink.rejected, (state, action) => {
        console.log("❌ getCandidateAssessmentLink: REJECTED", action.payload);
        state.assessmentLink.loading = false;
        state.assessmentLink.error = action.payload;
        state.assessmentLink.success = false;
        state.assessmentLink.data = null;
      });

    // Get Candidate Result
    builder
      .addCase(getCandidateResult.pending, (state) => {
        console.log("⏳ getCandidateResult: PENDING");
        state.candidateResult.loading = true;
        state.candidateResult.error = null;
        state.candidateResult.success = false;
        state.candidateResult.data = null;
      })
      .addCase(getCandidateResult.fulfilled, (state, action) => {
        console.log("✅ getCandidateResult: FULFILLED", action.payload);
        state.candidateResult.loading = false;
        state.candidateResult.data = action.payload;
        state.candidateResult.success = true;
        state.candidateResult.error = null;
      })
      .addCase(getCandidateResult.rejected, (state, action) => {
        console.log("❌ getCandidateResult: REJECTED", action.payload);
        state.candidateResult.loading = false;
        state.candidateResult.error = action.payload;
        state.candidateResult.success = false;
        state.candidateResult.data = null;
      });
  },
});

export const {
  clearCreateCandidateState,
  clearCandidateList,
  clearAddUpdateQuotaState,
  clearCancelCandidateState,
  clearAssessmentLinkState,
  clearCandidateResultState,
} = interviewCandidateSlice.actions;

export default interviewCandidateSlice.reducer;
